import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TOTAL_STEPS } from "../constants";
import { mapPhase } from "../labels";
import { HINT, PANEL, SPLIT, SPLIT_LEFT, SPLIT_RIGHT, SPLIT_SOLO } from "../styles";
import type { HeartbeatPreview } from "../types";
import { PreviewPane } from "./PreviewPane";
import { ProgressStrip } from "./ProgressStrip";

type Props = {
  preview?: HeartbeatPreview;
  previewPlaceholder: string;
  /** false drops the preview column and gives the settings the full width */
  showPreview: boolean;
  step: number;
  meta: unknown;
  /** the settings rows, rendered by the dock root — see the note on DockBar */
  settings: ReactNode;
  /** the Reset row, likewise */
  footer: ReactNode;
};

/**
 * The expanded area: preview beside the settings column (or the settings column alone,
 * full width, while the preview is switched off), Reset below a divider.
 *
 * Follows DockBar: this component owns the layout, not what sits in it. The settings
 * and the footer come in as nodes because every value they need is already in
 * HeadsetControlDock and none of it is read here — only placed.
 *
 * Only rendered while the selected headset is online over a live socket (see `open`
 * in HeadsetControlDock), so there is no offline variant here.
 */
export function DockPanel({
  preview,
  previewPlaceholder,
  showPreview,
  step,
  meta,
  settings,
  footer,
}: Props) {
  const { t } = useTranslation();
  const hasStep = step > 0;

  const settingsColumn = (
    <div className={SPLIT_RIGHT}>
      {hasStep && (
        <div className="flex-none flex flex-col gap-[8px]">
          <div className="flex items-baseline justify-between gap-[12px] text-[11px] font-normal">
            <span className="font-semibold">{mapPhase(t, step)}</span>
            <span className="text-[#949595] whitespace-nowrap">
              {t("HeadsetDock.StepOf", { step, total: TOTAL_STEPS })}
            </span>
          </div>
          <ProgressStrip step={step} />
        </div>
      )}

      {settings}

      {meta != null && (
        <div className={HINT}>
          {typeof meta === "string" ? meta : JSON.stringify(meta)}
        </div>
      )}
    </div>
  );

  return (
    <div className={PANEL}>
      {/* Both columns end on the same line: the preview stretches to the height of the
          settings rows, down to the divider and no further. min-h keeps the panel the
          same height running vs idle so the dock never jumps — it is a magic number,
          and adding a settings row will outgrow it. */}
      {showPreview ? (
        <div className={SPLIT}>
          <div className={SPLIT_LEFT}>
            <PreviewPane preview={preview} placeholder={previewPlaceholder} />
          </div>
          {settingsColumn}
        </div>
      ) : (
        <div className={SPLIT_SOLO}>{settingsColumn}</div>
      )}

      {footer}
    </div>
  );
}
